// BrainMonitor — Vella's Cognitive Brain Monitor, wired LIVE to the real backend.
// The Test Console calls the authenticated aiCustom() client (same-origin
// /api/ai/custom, real Bearer session) — so replies are genuinely Vella's, with
// no token paste and no CORS. The neural-network canvas, vitals, and reasoning
// trace are illustrative telemetry that surges toward the routed region on a
// real call. Self-contained; safe to drop into App.jsx's `views` map.
import React, { useState, useRef, useEffect, useCallback } from "react";
import { aiCustom } from "../api";

const FONT = '-apple-system,BlinkMacSystemFont,"Inter",system-ui,sans-serif';
const MONO = 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace';
const SYS = "You are Vella 2.0, AMPLIFX's AI marketing agent. Be specific, concise, and practical.";

const KNOWLEDGE = [
  ["SEO & Local Search",93],["Social Media Strategy",96],["Email Marketing",90],["Paid Ads / PPC",91],
  ["Copywriting & Messaging",95],["Brand & Positioning",88],["Analytics & Attribution",92],["Content Strategy",94],
  ["Conversion Optimization",89],["Influencer & UGC",87],["Video & Reels",90],["Marketing Automation",91],
  ["Reputation & Reviews",86],["Landing Pages & Web",92],
];
const FOUNDATION = [
  ["Consumer Psychology",95],["Behavioral Economics",93],["Persuasion & Rhetoric",96],["Statistics & Experiments",92],
  ["Data Science & Attribution",90],["Finance & Unit Economics",89],["Storytelling & Narrative",95],["Design & Visual Theory",91],
  ["Linguistics & Localization",93],["Business Strategy & GTM",92],["Market Research",90],["Industry Verticals",88],
  ["Culture & Trends",91],["Law, Ethics & Compliance",87],["Negotiation & Sales",90],
];
const CAPABILITIES = [
  ["Content Generation","ONLINE"],["Campaign Planning","ONLINE"],["Ad Optimization","ONLINE"],["Audience Targeting","ONLINE"],
  ["Performance Analysis","ONLINE"],["A/B Testing","ONLINE"],["Automation & Scheduling","ONLINE"],["Website Generation","ONLINE"],
  ["Image & Creative Studio","ONLINE"],["Social Publishing","ONLINE"],["SEO Audits","ONLINE"],["Email Sequences","ONLINE"],
  ["Review Management","ONLINE"],["Competitor Analysis","ONLINE"],["Voice & Phone","BETA"],["Video Generation","BETA"],
];
const REGIONS = [
  ["Perception",210,110,"#3b82f6"],["Creativity",520,120,"#c026d3"],["Reasoning",170,230,"#6366f1"],
  ["Memory",360,250,"#22d3ee"],["Action",560,250,"#f59e0b"],["Audience",470,300,"#22c55e"],
  ["Language",210,330,"#14b8a6"],["Decision",330,360,"#8b5cf6"],["Analytics",450,380,"#0ea5e9"],
];
const EDGES = [[0,2],[0,3],[2,3],[3,6],[3,7],[3,5],[3,4],[5,4],[1,4],[1,3],[7,8],[6,7],[5,8],[2,1]];
const CHIPS = [
  "Plan a 2-week Instagram campaign for a new coffee shop",
  "Write a promo email for a 20% off weekend sale",
  "Why are my Google Ads getting clicks but no conversions?",
  "Build a 30-day content calendar for a local gym",
  'Improve my Google rank for "best tacos near me"',
  "Get more 5-star reviews for my salon",
];

function regionFor(q){
  const t=q.toLowerCase(), R=re=>re.test(t);
  if(R(/why|not (working|converting)|underperform|conversion|roas|drop|analytic/))return "Analytics";
  if(R(/email|newsletter|sequence/))return "Language";
  if(R(/seo|rank|search|keyword|near me|organic/))return "Reasoning";
  if(R(/review|reputation|star/))return "Audience";
  if(R(/calendar|content|post|reel|caption|stor/))return "Creativity";
  if(R(/image|logo|graphic|video|creative|design|brand/))return "Creativity";
  if(R(/audience|target|segment|persona|customer/))return "Audience";
  if(R(/budget|spend|cost|pric/))return "Decision";
  if(R(/campaign|launch|\bad\b|ads|advertis|promot|ppc/))return "Action";
  if(R(/weather|news|today|time|score/))return "Perception";
  return "Reasoning";
}

const CSS = `
.bm-wrap{font-family:${FONT};color:#1e293b}
.bm-grid{display:grid;grid-template-columns:280px minmax(0,1fr) 320px;gap:16px;align-items:start}
.bm-bottom{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;margin-top:16px}
.bm-card{background:#fff;border:1px solid rgba(37,99,235,.12);border-radius:18px;box-shadow:0 10px 30px -18px rgba(37,99,235,.35),0 2px 6px -4px rgba(37,99,235,.12)}
.bm-lbl{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#64748b;margin:0 0 12px}
.bm-lbl::before{content:"";width:7px;height:7px;border-radius:50%;background:#2563eb}
.bm-bar{height:5px;border-radius:999px;background:#eef2f7;overflow:hidden;grid-column:1/-1}
.bm-bar i{display:block;height:100%;border-radius:999px;background:linear-gradient(90deg,#60a5fa,#2563eb)}
.bm-chip{font-size:12.5px;font-weight:500;color:#1e293b;background:#fff;border:1px solid rgba(37,99,235,.12);border-radius:999px;padding:9px 14px;cursor:pointer;transition:.15s}
.bm-chip:hover{border-color:#bfdbfe;box-shadow:0 4px 12px -6px rgba(37,99,235,.4)}
.bm-input{flex:1;min-width:0;font-family:${MONO};font-size:13px;padding:13px 15px;border-radius:12px;border:1px solid rgba(37,99,235,.12);outline:none;background:#fcfdff;color:#1e293b}
.bm-run{flex:none;border:none;cursor:pointer;color:#fff;font-weight:700;font-size:12px;letter-spacing:.1em;padding:13px 22px;border-radius:12px;background:linear-gradient(135deg,#93b4fb,#6366f1)}
.bm-run:disabled{opacity:.6;cursor:default}
@media (max-width:1180px){.bm-grid{grid-template-columns:1fr}.bm-bottom{grid-template-columns:1fr}}
`;

function Gauge({ val, label }){
  const r=30, c=2*Math.PI*r, off=c*(1-val/100);
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
      <svg width="86" height="86" viewBox="0 0 86 86">
        <circle cx="43" cy="43" r={r} fill="none" stroke="#eef2f7" strokeWidth="7"/>
        <circle cx="43" cy="43" r={r} fill="none" stroke="#2563eb" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 43 43)"/>
        <text x="43" y="49" textAnchor="middle" style={{fontFamily:MONO,fontSize:20,fontWeight:700,fill:"#1e293b"}}>{val}</text>
      </svg>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:".08em",color:"#94a3b8",textTransform:"uppercase"}}>{label}</div>
    </div>
  );
}

export default function BrainMonitor(){
  const canvasRef = useRef(null);
  const focusRef = useRef({ ri:-1, until:0 });
  const countsRef = useRef({ neurons:0, fires:0, signals:0 });
  const [counts, setCounts] = useState({ neurons:0, fires:0, signals:0 });
  const [input, setInput] = useState(CHIPS[0]);
  const [answer, setAnswer] = useState("awaiting input — ambient cognition");
  const [trace, setTrace] = useState([]);
  const [region, setRegion] = useState("Action");
  const [status, setStatus] = useState("standby"); // standby | live | offline
  const [busy, setBusy] = useState(false);

  // ── live neural-network engine (canvas) ──────────────────────────────────
  useEffect(() => {
    const cv = canvasRef.current; if(!cv) return;
    const ctx = cv.getContext("2d");
    const RG = REGIONS.map(r => ({ name:r[0], nx:r[1]/720, ny:r[2]/470, color:r[3] }));
    let W=0,H=0,DPR=1,hubs=[],neurons=[],neigh=[],signals=[],fireTimes=[],EDG=[],raf=0,lastT=0,spawnAcc=0,uiAcc=0,running=true;
    const hexA=(hex,a)=>{const n=parseInt(hex.slice(1),16);return `rgba(${(n>>16)&255},${(n>>8)&255},${n&255},${a})`;};
    const rr=(x,y,w,h,r)=>{ctx.beginPath();ctx.moveTo(x+r,y);ctx.arcTo(x+w,y,x+w,y+h,r);ctx.arcTo(x+w,y+h,x,y+h,r);ctx.arcTo(x,y+h,x,y,r);ctx.arcTo(x,y,x+w,y,r);ctx.closePath();};
    function layout(){
      const rect=cv.getBoundingClientRect();W=rect.width;H=rect.height;if(!W||!H)return;
      DPR=Math.min(window.devicePixelRatio||1,2);cv.width=W*DPR;cv.height=H*DPR;ctx.setTransform(DPR,0,0,DPR,0,0);
      const S=Math.min(W,H)/470;hubs=RG.map(r=>({...r,px:r.nx*W,py:r.ny*H}));neurons=[];const region=[];
      hubs.forEach((h,ri)=>{region[ri]=[];const start=neurons.length;
        neurons.push({ri,bx:h.px,by:h.py,r:5*S,ph:Math.random()*6.28,amp:1.4*S,hub:true,fire:0});region[ri].push(start);
        const cnt=h.name==="Action"?20:15;
        for(let i=0;i<cnt;i++){const a=Math.random()*6.28,rad=(16+Math.random()*62)*S;
          neurons.push({ri,bx:h.px+Math.cos(a)*rad,by:h.py+Math.sin(a)*rad*0.82,r:(1.5+Math.random()*1.9)*S,ph:Math.random()*6.28,amp:(2.2+Math.random()*2.4)*S,hub:false,fire:0});
          region[ri].push(neurons.length-1);}});
      EDG=[];hubs.forEach((h,ri)=>{const list=region[ri],hub=list[0];
        for(let k=1;k<list.length;k++){const i=list[k];if(Math.random()<0.45)EDG.push([hub,i]);
          else{let best=-1,bd=1e9;for(const j of list){if(j===i)continue;const dx=neurons[i].bx-neurons[j].bx,dy=neurons[i].by-neurons[j].by,d=dx*dx+dy*dy;if(d<bd){bd=d;best=j;}}if(best>=0)EDG.push([i,best]);}}});
      EDGES.forEach(([a,b])=>{const pa=region[a],pb=region[b];EDG.push([pa[(Math.random()*pa.length)|0],pb[(Math.random()*pb.length)|0]]);if(Math.random()<0.5)EDG.push([pa[(Math.random()*pa.length)|0],pb[(Math.random()*pb.length)|0]]);});
      neigh=neurons.map(()=>[]);EDG.forEach(([a,b])=>{neigh[a].push(b);neigh[b].push(a);});signals=[];
      countsRef.current.neurons=neurons.length;
    }
    const pos=(n,t)=>[n.bx+Math.cos(t*0.0006+n.ph)*n.amp,n.by+Math.sin(t*0.0007+n.ph)*n.amp];
    function spawn(a,b){if(a==null){const e=EDG[(Math.random()*EDG.length)|0];a=e[0];b=e[1];}signals.push({a,b,t:0,sp:0.006+Math.random()*0.013,color:hubs[neurons[a].ri].color});}
    function frame(now){
      if(!running)return; if(!lastT)lastT=now;const dt=Math.min(now-lastT,50);lastT=now;
      if(!W){raf=requestAnimationFrame(frame);return;}
      ctx.clearRect(0,0,W,H);const S=Math.min(W,H)/470;
      hubs.forEach(h=>{const big=(h.name==="Action"?120:78)*S;const g=ctx.createRadialGradient(h.px,h.py,0,h.px,h.py,big);g.addColorStop(0,hexA(h.color,0.13));g.addColorStop(1,hexA(h.color,0));ctx.fillStyle=g;ctx.beginPath();ctx.arc(h.px,h.py,big,0,6.29);ctx.fill();});
      ctx.lineWidth=0.6;for(const [a,b] of EDG){const A=pos(neurons[a],now),B=pos(neurons[b],now);ctx.strokeStyle=hexA(hubs[neurons[a].ri].color,0.16);ctx.beginPath();ctx.moveTo(A[0],A[1]);ctx.lineTo(B[0],B[1]);ctx.stroke();}
      for(let s=signals.length-1;s>=0;s--){const sg=signals[s];sg.t+=sg.sp*(dt/16.7);const A=pos(neurons[sg.a],now),B=pos(neurons[sg.b],now);const x=A[0]+(B[0]-A[0])*sg.t,y=A[1]+(B[1]-A[1])*sg.t;const tt=Math.max(0,sg.t-0.16),x2=A[0]+(B[0]-A[0])*tt,y2=A[1]+(B[1]-A[1])*tt;const gr=ctx.createLinearGradient(x2,y2,x,y);gr.addColorStop(0,hexA(sg.color,0));gr.addColorStop(1,hexA(sg.color,0.85));ctx.strokeStyle=gr;ctx.lineWidth=1.7;ctx.beginPath();ctx.moveTo(x2,y2);ctx.lineTo(x,y);ctx.stroke();ctx.fillStyle=sg.color;ctx.shadowColor=sg.color;ctx.shadowBlur=9;ctx.beginPath();ctx.arc(x,y,2.3,0,6.29);ctx.fill();ctx.shadowBlur=0;if(sg.t>=1){neurons[sg.b].fire=now;fireTimes.push(now);signals.splice(s,1);if(signals.length<30&&Math.random()<0.62){const nb=neigh[sg.b];if(nb.length)spawn(sg.b,nb[(Math.random()*nb.length)|0]);}}}
      for(const n of neurons){const P=pos(n,now);const fa=now-n.fire,firing=fa<600;if(firing){const k=fa/600;ctx.strokeStyle=hexA(hubs[n.ri].color,0.5*(1-k));ctx.lineWidth=1.4;ctx.beginPath();ctx.arc(P[0],P[1],n.r+2+k*16,0,6.29);ctx.stroke();}ctx.fillStyle=firing?"#fff":hexA(hubs[n.ri].color,n.hub?0.95:0.6);if(n.hub){ctx.shadowColor=hubs[n.ri].color;ctx.shadowBlur=10;}ctx.beginPath();ctx.arc(P[0],P[1],firing?n.r+1.3:n.r,0,6.29);ctx.fill();ctx.shadowBlur=0;if(n.hub){ctx.strokeStyle="#fff";ctx.lineWidth=1.5;ctx.beginPath();ctx.arc(P[0],P[1],n.r,0,6.29);ctx.stroke();}}
      ctx.font='700 14px '+FONT;ctx.textAlign="center";hubs.forEach(h=>{const w=ctx.measureText(h.name).width,ry=h.py-30;ctx.fillStyle="rgba(255,255,255,0.78)";rr(h.px-w/2-7,ry-13,w+14,20,7);ctx.fill();ctx.fillStyle=h.color;ctx.fillText(h.name,h.px,ry+1);});
      const fc=focusRef.current;spawnAcc+=dt;const target=fc.until>now?30:22;
      if(signals.length<target&&spawnAcc>40){spawnAcc=0;spawn();if(fc.ri>=0&&fc.until>now){const into=EDG.filter(e=>neurons[e[1]].ri===fc.ri);if(into.length){const e=into[(Math.random()*into.length)|0];spawn(e[0],e[1]);}}}
      uiAcc+=dt;if(uiAcc>240){uiAcc=0;const cut=now-1000;fireTimes=fireTimes.filter(t=>t>cut);countsRef.current.fires=fireTimes.length;countsRef.current.signals=signals.length;setCounts({...countsRef.current});}
      raf=requestAnimationFrame(frame);
    }
    layout();raf=requestAnimationFrame(frame);
    const ro=new ResizeObserver(()=>layout());ro.observe(cv);
    return ()=>{running=false;cancelAnimationFrame(raf);ro.disconnect();};
  }, []);

  // ── live backend call (authenticated, same-origin) ───────────────────────
  const run = useCallback(async () => {
    const q = input.trim(); if(!q || busy) return;
    const reg = regionFor(q);
    setRegion(reg);
    focusRef.current = { ri: REGIONS.findIndex(r=>r[0]===reg), until: performance.now()+9000 };
    setBusy(true); setAnswer("routing to Vella…"); setTrace([]);
    const t0 = performance.now();
    try{
      const res = await aiCustom(SYS, q, []);
      const reply = (res && res.reply) || "(empty reply)";
      const dt = Math.round(performance.now()-t0);
      setStatus("live"); setAnswer(reply);
      setTrace([
        ["REASON", `Intent parsed for "${q}".`, "—"],
        ["ANALYZE", `Routed to ${reg} via /api/ai/custom (live).`, "—"],
        ["GENERATE", `Live response received in ${dt}ms.`, dt+"ms"],
        ["ANSWER", reply.slice(0,170)+(reply.length>170?"…":""), "live"],
      ]);
    }catch(err){
      setStatus("offline");
      const msg = (err && err.message) || "request failed";
      setAnswer("Couldn't reach Vella's brain — "+msg);
      setTrace([["ERROR", String(msg), "—"], ["HINT", "Check the backend service + your session, then retry.", "—"]]);
    }finally{ setBusy(false); }
  }, [input, busy]);

  const onKey = (e) => { if(e.key==="Enter"){ e.preventDefault(); run(); } };

  const badge = status==="live"
    ? { t:"LIVE", bg:"#065f46", fg:"#fff", dot:"#34d399" }
    : status==="offline"
    ? { t:"OFFLINE", bg:"#7f1d1d", fg:"#fff", dot:"#f87171" }
    : { t:"STANDBY", bg:"#0b1220", fg:"#fff", dot:"#94a3b8" };

  const kdRow = ([n,p]) => (
    <div key={n} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:6,alignItems:"center"}}>
      <span style={{fontSize:13,fontWeight:500}}>{n}</span>
      <span style={{fontSize:12,fontFamily:MONO,color:"#64748b"}}>{p}%</span>
      <span className="bm-bar"><i style={{width:p+"%"}}/></span>
    </div>
  );
  const kdHead = (t) => <div key={t} style={{fontSize:10,fontWeight:800,letterSpacing:".1em",color:"#94a3b8",textTransform:"uppercase",margin:"4px 0 0"}}>{t}</div>;

  return (
    <div className="bm-wrap">
      <style>{CSS}</style>

      {/* top bar */}
      <div className="bm-card" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,padding:"14px 18px",marginBottom:16,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:38,height:38,borderRadius:11,background:"#0b1220",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800}}>A</div>
          <div>
            <div style={{fontSize:14,fontWeight:800,letterSpacing:".14em"}}>VELLA·BRAIN</div>
            <div style={{fontSize:10,letterSpacing:".18em",color:"#94a3b8",fontWeight:700}}>COGNITIVE MONITOR</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:11,fontWeight:700,color:badge.fg,background:badge.bg,borderRadius:999,padding:"7px 13px"}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:badge.dot}}/>{badge.t}
          </span>
          <span style={{fontSize:11,fontWeight:700,color:"#64748b",background:"#fff",border:"1px solid rgba(37,99,235,.12)",borderRadius:999,padding:"7px 13px"}}>MODEL <b style={{color:"#1e293b",fontFamily:MONO}}>Vella-2.0</b></span>
          <span style={{fontSize:11,fontWeight:700,color:"#64748b",background:"#fff",border:"1px solid rgba(37,99,235,.12)",borderRadius:999,padding:"7px 13px"}}>STATE <b style={{color:"#1e293b",fontFamily:MONO}}>{busy?"THINKING":"AMBIENT"}</b></span>
        </div>
      </div>

      <div className="bm-grid">
        {/* left */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div className="bm-card" style={{padding:16}}>
            <div className="bm-lbl">Knowledge Domains</div>
            <div style={{display:"flex",flexDirection:"column",gap:11}}>
              {kdHead("Marketing")}{KNOWLEDGE.map(kdRow)}
              {kdHead("Foundational · Opus 4.8")}{FOUNDATION.map(kdRow)}
            </div>
          </div>
          <div className="bm-card" style={{padding:16}}>
            <div className="bm-lbl">Capabilities <span style={{marginLeft:"auto",color:"#94a3b8",fontWeight:700}}>{CAPABILITIES.length} total</span></div>
            <div style={{display:"flex",flexDirection:"column"}}>
              {CAPABILITIES.map(([n,s],i)=>(
                <div key={n} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 0",borderTop:i?"1px dashed #eef0f4":"none"}}>
                  <span style={{display:"flex",alignItems:"center",gap:9,fontSize:13.5,fontWeight:500}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:s==="BETA"?"#f59e0b":"#22c55e",boxShadow:`0 0 0 3px ${s==="BETA"?"rgba(245,158,11,.15)":"rgba(34,197,94,.15)"}`}}/>{n}
                  </span>
                  <span style={{fontSize:10,fontWeight:800,letterSpacing:".1em",color:s==="BETA"?"#b45309":"#16a34a"}}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* center */}
        <div className="bm-card">
          <div style={{padding:"12px 16px",fontFamily:MONO,fontSize:13,lineHeight:1.55,color:"#64748b",maxHeight:120,overflowY:"auto"}}>
            ANSWER — <b style={{color:"#2563eb",fontWeight:700,whiteSpace:"pre-wrap"}}>{answer}</b>
          </div>
          <div style={{position:"relative",padding:8}}>
            <div style={{position:"absolute",top:18,left:"50%",transform:"translateX(-50%)",fontSize:30,fontWeight:800,letterSpacing:".5em",color:"rgba(37,99,235,.05)",pointerEvents:"none",userSelect:"none"}}>NEURAL CORE</div>
            <div style={{position:"absolute",top:14,right:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 16px",background:"rgba(255,255,255,.85)",border:"1px solid rgba(37,99,235,.12)",borderRadius:12,padding:"10px 12px"}}>
              {REGIONS.map(r=>(<span key={r[0]} style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"#64748b",fontWeight:600}}><span style={{width:8,height:8,borderRadius:"50%",background:r[3]}}/>{r[0]}</span>))}
            </div>
            <canvas ref={canvasRef} style={{display:"block",width:"100%",height:540,borderRadius:14}}/>
          </div>
          <div style={{display:"flex",gap:22,padding:"4px 16px 14px",fontSize:12,color:"#64748b",fontFamily:MONO}}>
            <span>◆ <b style={{color:"#1e293b"}}>{counts.neurons}</b> neurons</span>
            <span>◆ <b style={{color:"#1e293b"}}>{counts.fires}</b> fires/s</span>
            <span>◆ <b style={{color:"#1e293b"}}>{counts.signals}</b> active signals</span>
          </div>
        </div>

        {/* right */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div className="bm-card" style={{padding:16}}>
            <div className="bm-lbl">Vital Signs <span style={{marginLeft:"auto",fontSize:9,color:"#cbd5e1",fontWeight:700,letterSpacing:".05em"}}>ILLUSTRATIVE</span></div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              <Gauge val={96} label="Confidence"/><Gauge val={86} label="Cog Load"/><Gauge val={72} label="Focus"/>
            </div>
          </div>
          <div className="bm-card" style={{padding:16}}>
            <div className="bm-lbl">Region Focus</div>
            <div style={{fontFamily:MONO,fontSize:22,fontWeight:700,color:(REGIONS.find(r=>r[0]===region)||[])[3]||"#2563eb"}}>{region}</div>
            <div style={{fontSize:12,color:"#64748b",marginTop:6}}>Last command routed to the <b>{region}</b> region of Vella's brain.</div>
          </div>
        </div>
      </div>

      {/* bottom */}
      <div className="bm-bottom">
        <div className="bm-card" style={{padding:16}}>
          <div className="bm-lbl">Reasoning Trace</div>
          <div>
            {trace.length===0 && <div style={{fontSize:13,color:"#94a3b8"}}>Run a command to see the live trace.</div>}
            {trace.map(([st,tx,ms],i)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:"84px 1fr auto",gap:12,alignItems:"start",padding:"10px 0",borderTop:i?"1px solid #f1f3f7":"none",fontSize:13}}>
                <span style={{fontSize:11,fontWeight:800,letterSpacing:".08em",color:st==="ERROR"?"#dc2626":"#2563eb"}}>{st}</span>
                <span style={{color:st==="ANSWER"?"#2563eb":"#1e293b",fontWeight:st==="ANSWER"?600:400,lineHeight:1.45,fontFamily:st==="ANSWER"?MONO:FONT,fontSize:st==="ANSWER"?12.5:13}}>{tx}</span>
                <span style={{fontFamily:MONO,fontSize:11,color:"#94a3b8"}}>{ms}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bm-card" style={{padding:16}}>
          <div className="bm-lbl">Test Console <span style={{marginLeft:"auto",fontSize:9,color:"#16a34a",fontWeight:800,letterSpacing:".06em"}}>LIVE · /api/ai/custom</span></div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,margin:"4px 0 14px"}}>
            {CHIPS.map(c=>(<span key={c} className="bm-chip" onClick={()=>{ setInput(c); setTimeout(run,0); }}>{c}</span>))}
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <input className="bm-input" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={onKey} placeholder="Ask Vella anything…"/>
            <button className="bm-run" onClick={run} disabled={busy}>{busy?"…":"RUN TEST"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
